import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Image, Platform } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { WorkStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { PRODUCT_CATEGORIES } from '../../data/jobCategories';
import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import ThemedText from '../../components/common/ThemedText';
import { CustomPicker } from '../../components/common/CustomPicker';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';

type EditProductNavigationProp = StackNavigationProp<WorkStackParamList, 'EditProduct'>;
type EditProductRouteProp = RouteProp<WorkStackParamList, 'EditProduct'>;

const EditProductScreen: React.FC = () => {
  const navigation = useNavigation<EditProductNavigationProp>();
  const route = useRoute<EditProductRouteProp>();
  const { theme } = useTheme();
  const { updateJob, fetchJobById } = useWork();
  const { productId } = route.params;
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [errors, setErrors] = useState({ title: false, category: false, description: false, price: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  useEffect(() => {
    loadProduct();
  }, [productId]);

  useEffect(() => {
    console.log('🔍 [EditProductScreen] spacing => headerHeight:', headerHeight, 'bottomInset:', bottomInset, 'keyboardHeight:', keyboardHeight);
  }, [headerHeight, bottomInset, keyboardHeight]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const product = await fetchJobById(productId);
      if (product) {
        setTitle(product.title);
        setCategory(product.category);
        setDescription(product.description);
        setPrice(product.price?.toString() || '');
        setImages(product.images || []);
      }
    } catch (error) {
      showErrorToast('Failed to load product details');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (uri: string): Promise<string | null> => {
    try {
      const token = await storage.getToken();
      if (!token) {
        throw new Error('No auth token');
      }

      const filename = uri.split('/').pop() || 'image.jpg';
      const match = /\.(\w+)$/.exec(filename);
      const type = match ? `image/${match[1]}` : 'image/jpeg';

      const formData = new FormData();
      
      // Handle web differently
      if (Platform.OS === 'web') {
        const response = await fetch(uri);
        const blob = await response.blob();
        const file = new File([blob], filename, { type });
        formData.append('image', file);
      } else {
        formData.append('image', {
          uri,
          name: filename,
          type,
        } as any);
      }

      const response = await fetch(buildApiUrl('/api/upload/image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Image upload error:', error);
      return null;
    }
  };

  const pickImage = async () => {
    if (images.length >= 1) {
      showErrorToast('You can upload only 1 image');
      return;
    }

    // Photo picker on Android 13+ doesn't need READ_MEDIA_* permissions
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setUploadingImage(true);
      const uploadedUrl = await uploadImage(result.assets[0].uri);
      setUploadingImage(false);

      if (uploadedUrl) {
        setImages([uploadedUrl]);
      } else {
        showErrorToast('Failed to upload image. Please try again.');
      }
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleUpdateProduct = async () => {
    setErrors({ title: false, category: false, description: false, price: false });

    const newErrors = {
      title: !title.trim(),
      category: !category.trim(),
      description: !description.trim(),
      price: !price.trim(),
    };

    setErrors(newErrors);

    if (newErrors.title || newErrors.category || newErrors.description || newErrors.price) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateJob(productId, {
        title: title.trim(),
        category,
        description: description.trim(),
        price: parseFloat(price.replace(/[^0-9.]/g, '')),
        images: images,
      });

      showSuccessToast('Product updated successfully!');
      navigation.goBack();
    } catch (error) {
      showErrorToast('Failed to update product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={Platform.OS === 'ios' ? ['left', 'right'] : ['top', 'left', 'right']}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
            <Ionicons name="close" size={28} color={theme.text} />
          </TouchableOpacity>
          <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Edit Product</ThemedText>
          <View style={styles.headerButton} />
        </View>
        <View style={[styles.loadingContainer, { backgroundColor: theme.background }]}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={Platform.OS === 'ios' ? ['left', 'right'] : ['top', 'left', 'right']}>
      <View
        style={[styles.header, { borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          if (height !== headerHeight) {
            setHeaderHeight(height);
          }
        }}
      >
        <TouchableOpacity onPress={handleCancel} style={styles.headerButton}>
          <Ionicons name="close" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Edit Product</ThemedText>
        <View style={styles.headerButton} />
      </View>

      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Product Name *</ThemedText>
          <TextInput
            style={[
              styles.input, 
              { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
              errors.title && { borderWidth: 2, borderColor: theme.error }
            ]}
            placeholder="e.g., iPhone 13 Pro"
            value={title}
            onChangeText={(text) => {
              setTitle(text);
              if (errors.title) setErrors({ ...errors, title: false });
            }}
            placeholderTextColor={theme.textSecondary}
          />
          {errors.title && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Category *</ThemedText>
          <CustomPicker
            selectedValue={category || undefined}
            onValueChange={(value) => {
              setCategory(value || '');
              if (errors.category) setErrors({ ...errors, category: false });
            }}
            items={[
              { label: 'Select a category', value: undefined },
              ...PRODUCT_CATEGORIES.map(cat => ({ label: cat, value: cat }))
            ]}
            placeholder="Select a category"
            searchable={true}
            searchPlaceholder="Search categories..."
          />
          {errors.category && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Price *</ThemedText>
          <TextInput
            style={[
              styles.input, 
              { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
              errors.price && { borderWidth: 2, borderColor: theme.error }
            ]}
            placeholder="e.g., 45000"
            value={price}
            onChangeText={(text) => {
              setPrice(text);
              if (errors.price) setErrors({ ...errors, price: false });
            }}
            keyboardType="numeric"
            placeholderTextColor={theme.textSecondary}
          />
          {errors.price && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Description *</ThemedText>
          <TextInput
            style={[
              styles.input, 
              styles.textArea, 
              { backgroundColor: theme.backgroundSecondary, color: theme.text, borderColor: theme.border },
              errors.description && { borderWidth: 2, borderColor: theme.error }
            ]}
            placeholder="Describe the product condition, features, etc..."
            value={description}
            onChangeText={(text) => {
              setDescription(text);
              if (errors.description) setErrors({ ...errors, description: false });
            }}
            multiline
            numberOfLines={6}
            textAlignVertical="top"
            placeholderTextColor={theme.textSecondary}
          />
          {errors.description && (
            <ThemedText style={[styles.errorText, { color: theme.error }]}>This field is required</ThemedText>
          )}
        </View>

        <View style={styles.formGroup}>
          <ThemedText style={[styles.label, { color: theme.text }]}>Photo (Optional)</ThemedText>
          <ThemedText style={[styles.helperText, { color: theme.textSecondary }]}>
            Add 1 photo for your product listing.
          </ThemedText>
          
          <View style={styles.imageGrid}>
            {images.map((uri, index) => (
              <View key={index} style={styles.imageContainer}>
                <Image source={{ uri }} style={styles.imagePreview} />
                <TouchableOpacity 
                  style={[styles.removeImageButton, { backgroundColor: theme.error }]}
                  onPress={() => removeImage(index)}
                >
                  <Ionicons name="close" size={16} color="#FFF" />
                </TouchableOpacity>
              </View>
            ))}
            
            {images.length < 1 && (
              <TouchableOpacity 
                style={[styles.addImageButton, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                onPress={pickImage}
                disabled={uploadingImage}
              >
                {uploadingImage ? (
                  <ActivityIndicator color={theme.accent} />
                ) : (
                  <>
                    <Ionicons name="camera-outline" size={32} color={theme.textSecondary} />
                    <ThemedText style={[styles.addImageText, { color: theme.textSecondary }]}>Add Photo</ThemedText>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity style={[styles.cancelButton, { borderColor: theme.border }]} onPress={handleCancel} disabled={isSubmitting}>
            <ThemedText style={[styles.cancelButtonText, { color: theme.textSecondary }]}>Cancel</ThemedText>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.postButton, { backgroundColor: theme.accent, opacity: isSubmitting ? 0.6 : 1 }]} 
            onPress={handleUpdateProduct}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <ActivityIndicator color={theme.textInverse} />
            ) : (
              <ThemedText style={[styles.postButtonText, { color: theme.textInverse }]}>Update Product</ThemedText>
            )}
          </TouchableOpacity>
        </View>
        <FormFooterSpacer
          headerHeight={headerHeight}
          bottomInset={bottomInset}
          keyboardHeight={keyboardHeight}
        />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  formGroup: {
    marginBottom: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    borderWidth: 1,
  },
  pickerInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
  },
  pickerText: {
    fontSize: 16,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  helperText: {
    fontSize: 12,
    marginTop: 4,
    marginBottom: 12,
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  imageContainer: {
    width: 100,
    height: 100,
    borderRadius: 8,
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  removeImageButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addImageButton: {
    width: 100,
    height: 100,
    borderRadius: 8,
    borderWidth: 2,
    borderStyle: 'dashed',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addImageText: {
    fontSize: 12,
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  postButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  postButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default EditProductScreen;
